import { defineStore } from "pinia";
import { computed, toRefs, reactive, watch } from "vue";
import type { IDomainPaymentMethod } from "./../domain/payment-method";
import type { IDomainOutletAddress } from "~/domain/outlet-address";
import type { IDomainMenu } from "~/domain/menu";
import type { IDomainOpeningHour } from "~/domain/opening-hours";
import type { IDomainOrderingMode } from "~/domain/ordering-mode";
import type { IDomainOutletState } from "~/domain/outlet-state";
import type { IDomainSalesChannel } from "~/domain/sales-channels";
import {
  useAnalytics,
  useBugReporter,
  useDirectBackend,
  useFeatureFlag,
} from "#imports";

type IOrderingModeConfig = {
  isEnabled: boolean;
  acceptsPackaging: boolean;
  acceptsAsapOrders: boolean;
  acceptsPreOrders: boolean;
  outletState: IDomainOutletState;
  paymentMethods: IDomainPaymentMethod[];
  menus: IDomainMenu[];
  preparationMinutes: number;
  estimatedCompletionMinutes: number;
  slotLength: number;
  openingHours: IDomainOpeningHour[];
};

// Define the state interface
type IState = {
  isLoaded: boolean;
  id: string;
  name: string;
  slug: string;
  phone?: string;
  cover?: string;
  isTaxIncluded: boolean;
  hasDelivery: boolean;
  hasPickup: boolean;
  address: IDomainOutletAddress;
  activeOrderingMode?: IDomainOrderingMode;
  orderingModes: {
    delivery: IOrderingModeConfig;
    pickup: IOrderingModeConfig;
  };
};

// Helper function to create the initial state
function createInitialState(): IState {
  return {
    isLoaded: false,
    id: "",
    name: "",
    slug: "",
    phone: undefined,
    cover: undefined,
    hasDelivery: false,
    hasPickup: false,
    address: {
      lat: 0,
      lng: 0,
      location: "",
    },
    isTaxIncluded: false,
    activeOrderingMode: undefined,
    orderingModes: {
      delivery: {
        isEnabled: false,
        acceptsPackaging: false,
        acceptsAsapOrders: true,
        acceptsPreOrders: true,
        outletState: "closed",
        paymentMethods: [],
        menus: [],
        preparationMinutes: 0,
        estimatedCompletionMinutes: 0,
        slotLength: 0,
        openingHours: [],
      },
      pickup: {
        isEnabled: false,
        acceptsPackaging: false,
        acceptsAsapOrders: true,
        acceptsPreOrders: true,
        outletState: "closed",
        paymentMethods: [],
        menus: [],
        preparationMinutes: 0,
        estimatedCompletionMinutes: 0,
        slotLength: 0,
        openingHours: [],
      },
    },
  };
}

export const useOutletStore = defineStore("outlet", () => {
  // Create a reactive state object using the initial state
  const state = reactive<IState>(createInitialState());

  // Helper function to reset the state to its initial values
  function reset() {
    Object.assign(state, createInitialState());
  }

  // Helper function to patch the state with a partial object
  function patch(patch: Partial<IState>) {
    Object.assign(state, patch);
  }

  const { outletRepo } = useDirectBackend();
  async function refreshPaymentMethods(
    orderingMode: IDomainOrderingMode,
    salesChannel: IDomainSalesChannel,
  ) {
    const res = await outletRepo.getPaymentMethods({
      outletId: state.id,
      orderingMode,
      salesChannel,
    });

    patch({
      orderingModes: {
        ...state.orderingModes,
        [orderingMode]: {
          ...state.orderingModes[orderingMode],
          paymentMethods: res.data,
        },
      },
    });
  }

  const orderingMode = computed(() => {
    if (!state.activeOrderingMode) return state.orderingModes.delivery;
    return state.orderingModes[state.activeOrderingMode];
  });

  const isClosed = computed(() => orderingMode.value.outletState === "closed");

  const { reportGlobalTags } = useBugReporter();
  const { setContextField } = useFeatureFlag();
  const { setSuperProperties } = useAnalytics();

  watch(
    () => state.id,
    () => {
      setSuperProperties({
        outletId: state.id || undefined,
        outletName: state.name || undefined,
        outletSlug: state.slug || undefined,
      });

      setContextField("outletId", state.id || undefined);

      reportGlobalTags({
        "outlet.id": state.id || undefined,
        "outlet.slug": state.slug || undefined,
      });
    },
  );

  return {
    ...toRefs(state),
    state: computed(() => state),
    patch,
    reset,
    isClosed,
    orderingMode,
    refreshPaymentMethods,
  };
});
